import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VerifoneService } from '@principle-theorem/ng-payments';
import {
  LoaderButtonComponent,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IPractice,
  IVerifoneTerminal,
  VerifonePairResult,
} from '@principle-theorem/principle-core/interfaces';
import { DocumentReference, getError, uid } from '@principle-theorem/shared';

interface IAddVerifoneTerminalFormData extends Pick<IVerifoneTerminal, 'name'> {
  serialNumber: string;
}

export interface IPairVerifoneTerminalDialogRequest {
  practiceRef: DocumentReference<IPractice>;
  testModeEnabled?: boolean;
}

@Component({
  selector: 'pr-pair-verifone-terminal-dialog',
  templateUrl: './pair-verifone-terminal-dialog.component.html',
  styleUrls: ['./pair-verifone-terminal-dialog.component.scss'],
  imports: [NgDialogModule, LoaderButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PairVerifoneTerminalDialogComponent {
  loading = signal(false);

  form = new TypedFormGroup<IAddVerifoneTerminalFormData>({
    name: new TypedFormControl<string>('', Validators.required),
    serialNumber: new TypedFormControl<string>('', Validators.required),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: IPairVerifoneTerminalDialogRequest,
    private _verifone: VerifoneService,
    private _snackBar: MatSnackBar,
    private _dialogRef: MatDialogRef<PairVerifoneTerminalDialogComponent>
  ) {}

  async submit(): Promise<void> {
    const data = this.form.getRawValue();
    if (this.form.invalid) {
      return;
    }

    this.loading.set(true);
    try {
      const terminal: Pick<IVerifoneTerminal, 'name' | 'uid'> = {
        name: data.name,
        uid: uid(true),
      };

      const response = await this._verifone.pairTerminal({
        serialNumber: data.serialNumber,
        practiceRef: this._data.practiceRef,
        terminal,
        testModeEnabled: this._data.testModeEnabled,
      });

      if (response.result !== VerifonePairResult.Success) {
        const errorMessage = this._getErrorMessage(response.result);
        this._snackBar.open(errorMessage, 'Dismiss', { duration: 5000 });
        return;
      }

      this._snackBar.open('Terminal paired successfully', 'Dismiss', {
        duration: 5000,
      });
      this._dialogRef.close();
    } catch (error) {
      this._snackBar.open(getError(error), 'Dismiss', { duration: 5000 });
    } finally {
      this.loading.set(false);
    }
  }

  private _getErrorMessage(result: VerifonePairResult): string {
    switch (result) {
      case VerifonePairResult.TerminalNotFound:
        return 'Terminal not found. Please check the serial number.';
      case VerifonePairResult.TerminalNotAvailable:
        return 'This terminal is already paired to another practice.';
      case VerifonePairResult.InvalidArgs:
        return 'Invalid terminal configuration. Please contact support.';
      case VerifonePairResult.Failure:
      default:
        return 'Failed to pair terminal. Please try again.';
    }
  }
}
